/**
 * Abgleich und Sync-Protokoll (KONZEPT.md §15.2, §15.4).
 *
 * Wohin die Statusanzeige der Kopfzeile fuehrt. Hier steht, was noch wartet,
 * was der Server abgewiesen hat -- und welche eigenen Buchungen beim Abgleich
 * von einer neueren ueberstimmt waren.
 *
 * Nichts davon verlangt eine Entscheidung (CLAUDE.md §3.3). Die neuere Buchung
 * hat gewonnen; hier steht nur, dass es so war. Wer das Geraet in der Hand
 * hat, weiss danach, dass seine Buchung nicht gilt, und warum.
 */

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { alsBuchung, nochmal } from "../api/warteschlange.js";
import { abgleichen, UHR_TOLERANZ_MS, useLaufzustand } from "../lokal/abgleich.js";
import { buchungKurz, standKurz, uhrzeit } from "../lokal/beschreiben.js";
import { useBestand, useLokal } from "../lokal/bestand.js";
import { db, metaLesen, type SyncHinweis } from "../lokal/datenbank.js";
import { Karte, Knopf, Ladeanzeige, Leer, Nummer, Rueckmeldung } from "../ui/bausteine.jsx";

async function protokollLaden() {
  const [wartende, abgelehnte, hinweise, letzter, abweichung] = await Promise.all([
    db.buchungen.where("zustand").equals("wartet").sortBy("angelegt_am"),
    db.buchungen.where("zustand").equals("abgelehnt").sortBy("angelegt_am"),
    db.hinweise.orderBy("id").toArray(),
    metaLesen("letzter_abgleich"),
    metaLesen("uhr_abweichung_ms"),
  ]);
  return { wartende, abgelehnte, hinweise, letzter, abweichung };
}

/** Mehrere Hinweise zum selben Artikel werden zusammengefasst (§15.4). */
function jeArtikel(hinweise: readonly SyncHinweis[]): SyncHinweis[][] {
  const gruppen = new Map<string, SyncHinweis[]>();
  for (const h of hinweise) {
    const liste = gruppen.get(h.artikel_id);
    if (liste === undefined) gruppen.set(h.artikel_id, [h]);
    else liste.push(h);
  }
  // Neueste zuerst -- innerhalb der Gruppe und zwischen den Gruppen.
  return [...gruppen.values()]
    .map((g) => g.sort((a, b) => b.buchung.erfasst_am.localeCompare(a.buchung.erfasst_am)))
    .sort((a, b) => (b[0]?.erkannt_am ?? "").localeCompare(a[0]?.erkannt_am ?? ""));
}

export function Abgleich() {
  const bestand = useBestand();
  const lauf = useLaufzustand();
  const protokoll = useLokal(protokollLaden, []);
  const [dauerhaft, setDauerhaft] = useState<boolean | null>(null);

  // Was beim Oeffnen ungelesen war, bleibt fuer diesen Besuch als „neu"
  // markiert. Gelesen ist es trotzdem sofort -- die Zahl in der Kopfzeile
  // soll verschwinden, sobald jemand hingesehen hat.
  const [neu] = useState(() => new Set<string>());
  useEffect(() => {
    void db.hinweise
      .where("gelesen")
      .equals(0)
      .toArray()
      .then(async (ungelesen) => {
        for (const h of ungelesen) neu.add(h.id);
        await db.hinweise.bulkPut(ungelesen.map((h) => ({ ...h, gelesen: 1 as const })));
      });
  }, [neu, protokoll?.hinweise.length]);

  useEffect(() => {
    void Promise.resolve()
      .then(() => globalThis.navigator.storage.persisted())
      .then(setDauerhaft)
      .catch(() => {
        setDauerhaft(null);
      });
  }, []);

  const gruppen = useMemo(() => jeArtikel(protokoll?.hinweise ?? []), [protokoll]);

  if (protokoll === undefined || bestand === undefined) return <Ladeanzeige was="Der Abgleich" />;

  const online = lauf.online && lauf.erreichbar;
  const uhrFalsch =
    protokoll.abweichung !== null && Math.abs(protokoll.abweichung) > UHR_TOLERANZ_MS;
  const artikelName = (id: string): { name: string; nummer: string } => {
    const a = bestand.artikel.get(id);
    return { name: a?.bezeichnung ?? "Unbekannter Artikel", nummer: a?.inventarnummer ?? "" };
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Abgleich</h1>

      {lauf.fehler !== null && (
        <Rueckmeldung art="warnung" kopf="Der Abgleich hakt" zusatz={lauf.fehler} />
      )}

      {uhrFalsch && (
        <Rueckmeldung
          art="warnung"
          kopf="Die Uhr dieses Geräts geht falsch"
          zusatz={`Sie weicht um etwa ${String(Math.round(Math.abs(protokoll.abweichung ?? 0) / 60_000))} Minuten von der Uhr des Servers ab. Welche Buchung gilt, hängt an der Uhrzeit — bitte in den Einstellungen des Geräts „Uhrzeit automatisch" einschalten.`}
        />
      )}

      <Karte>
        <p className="text-lg font-semibold">
          {!online
            ? "Offline"
            : lauf.laeuft
              ? "Synchronisiere …"
              : protokoll.wartende.length > 0
                ? "Online — Buchungen warten"
                : "Online — synchron"}
        </p>
        <p className="text-sm text-neutral-600 dark:text-neutral-300">
          {protokoll.letzter === null
            ? "Noch nie vollständig abgeglichen."
            : `Zuletzt abgeglichen ${uhrzeit(protokoll.letzter)}.`}{" "}
          Mit Netz gleicht das Gerät jede Minute von selbst ab.
        </p>
        <Knopf
          art="haupt"
          disabled={!lauf.online || lauf.laeuft}
          onClick={() => void abgleichen({ stammdaten: true })}
        >
          Jetzt abgleichen
        </Knopf>
        {dauerhaft === false && (
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Der Browser darf die Daten dieses Geräts bei Platzmangel löschen. Als App installiert
            (unter „Konto“) passiert das nicht.
          </p>
        )}
      </Karte>

      <Karte titel="Hinweise zur Synchronisation">
        {gruppen.length === 0 ? (
          <Leer>Keine. Alle eigenen Buchungen haben gegolten.</Leer>
        ) : (
          <ul className="flex flex-col gap-3">
            {gruppen.map((gruppe) => {
              const [neueste, ...weitere] = gruppe;
              if (neueste === undefined) return null;
              const { name, nummer } = artikelName(neueste.artikel_id);
              const zustand = bestand.zustaende.get(neueste.artikel_id);
              return (
                <li
                  key={neueste.artikel_id}
                  className="rounded-xl border-l-4 border-amber-500 bg-amber-50 p-3 dark:bg-amber-950"
                >
                  <p className="font-bold">
                    <Link to={`/artikel/${neueste.artikel_id}`} className="underline">
                      {name}
                    </Link>{" "}
                    <Nummer>({nummer})</Nummer>
                    {gruppe.some((h) => neu.has(h.id)) && (
                      <span className="ml-2 rounded-full bg-amber-500 px-2 text-xs text-white">
                        neu
                      </span>
                    )}
                  </p>
                  <p className="mt-1">
                    Deine Buchung „{buchungKurz(neueste.buchung, bestand)}“ von{" "}
                    {uhrzeit(neueste.buchung.erfasst_am)} wurde durch eine neuere Buchung ersetzt
                    {neueste.gewinner.benutzer_id !== neueste.buchung.benutzer_id &&
                      ` (${bestand.benutzer.get(neueste.gewinner.benutzer_id)?.anzeigename ?? "unbekannt"})`}
                    .
                  </p>
                  {weitere.length > 0 && (
                    <p className="mt-1 text-sm">
                      Dazu {weitere.length} weitere ältere Buchung{weitere.length === 1 ? "" : "en"}{" "}
                      dieses Artikels.
                    </p>
                  )}
                  {zustand !== undefined && (
                    <p className="mt-1 font-semibold">
                      Aktueller Stand: {standKurz(zustand, bestand)}
                      {zustand.letzte_buchung_am !== null &&
                        `, gebucht ${uhrzeit(zustand.letzte_buchung_am)}`}
                      .
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Karte>

      {protokoll.abgelehnte.length > 0 && (
        <Karte titel="Vom Server abgewiesen">
          <p className="text-sm text-neutral-600 dark:text-neutral-300">
            Diese Buchungen hat der Server nicht angenommen. Sie bleiben hier, bis jemand sie
            ansieht.
          </p>
          <ul className="flex flex-col gap-2">
            {protokoll.abgelehnte.map((w) => {
              const { name, nummer } = artikelName(w.buchung.artikel_id);
              return (
                <li key={w.id} className="rounded-xl bg-red-50 p-3 dark:bg-red-950">
                  <p className="font-semibold">
                    {name} <Nummer>({nummer})</Nummer>
                  </p>
                  <p className="text-sm">
                    {buchungKurz(alsBuchung(w), bestand)} · {uhrzeit(w.buchung.erfasst_am)}
                  </p>
                  <p className="text-sm text-red-800 dark:text-red-200">Grund: {w.grund}</p>
                  <Knopf onClick={() => void nochmal(w.id)} disabled={!lauf.online}>
                    Erneut senden
                  </Knopf>
                </li>
              );
            })}
          </ul>
        </Karte>
      )}

      <Karte titel="Wartet auf Übertragung">
        {protokoll.wartende.length === 0 ? (
          <Leer>Nichts. Alles ist beim Server.</Leer>
        ) : (
          <ul className="flex flex-col gap-1 text-sm">
            {protokoll.wartende.map((w) => {
              const { name, nummer } = artikelName(w.buchung.artikel_id);
              return (
                <li key={w.id}>
                  <span className="font-semibold">{name}</span> <Nummer>{nummer}</Nummer> ·{" "}
                  {buchungKurz(alsBuchung(w), bestand)} · {uhrzeit(w.buchung.erfasst_am)}
                </li>
              );
            })}
          </ul>
        )}
      </Karte>
    </div>
  );
}
